/* B5 — Workstage 2 (Feasibility) admin page.
   Four modules vertical stack (§4).  Authoritative preview →
   confirm-and-send for WS2 comms.  Advisory score/percentage/band
   is display-only; the admin decision is deliberate. */
import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { api, API } from "../../lib/api";
import MarkSentExternallyControl from "./MarkSentExternallyControl";

const CATEGORIES = [
    { key: "location", display: "Location",
      labels: ["Not suitable", "Borderline · review", "Suitable"] },
    { key: "project_scope", display: "Project scope",
      labels: ["Not suitable", "Requires review", "Suitable"] },
    { key: "budget_alignment", display: "Budget alignment",
      labels: ["Misaligned", "Uncertain", "Aligned"] },
    { key: "timing_readiness", display: "Timing / readiness",
      labels: ["Not realistically ready", "Delayed but viable",
                "Ready"] },
    { key: "planning_technical", display: "Planning / technical",
      labels: ["Material concern", "Further review required",
                "No material issue identified at this stage"] },
    { key: "information_sufficiency", display: "Information sufficiency",
      labels: ["Materially insufficient", "Partial · some gaps",
                "Sufficient"] },
];

const BAND_DISPLAY = {
    poor: "Poor", marginal: "Marginal", reasonable: "Reasonable",
    strong: "Strong", very_strong: "Very Strong",
};

const DECISIONS = [
    { value: "proceed", label: "Proceed" },
    { value: "further_review",
      label: "Further review or information required" },
    { value: "decline", label: "Decline" },
];

function CategoryCard({ cat, value, onChange, disabled }) {
    return (
        <div className="border border-[#E5E2DC] rounded-lg p-4 flex flex-col gap-2 bg-white"
             data-testid={`ws2-cat-${cat.key}`}>
            <div className="font-manrope font-medium text-[13px]">{cat.display}</div>
            <div className="flex flex-col gap-1.5">
                {[0, 1, 2].map(v => (
                    <label key={v}
                        className={`flex items-center gap-2 text-[13px] cursor-pointer px-2 py-1.5 rounded-md border ${
                            value === v
                                ? "border-charcoal bg-[#F5F3EE]"
                                : "border-transparent hover:bg-[#FBFAF6]"}`}>
                        <input type="radio" name={`ws2-cat-${cat.key}`}
                            checked={value === v}
                            disabled={disabled}
                            onChange={() => onChange(cat.key, v)}
                            data-testid={`ws2-cat-${cat.key}-score-${v}`}
                            className="accent-charcoal" />
                        <span className="text-[11px] text-[#666] font-mono w-3">{v}</span>
                        <span>{cat.labels[v]}</span>
                    </label>
                ))}
            </div>
        </div>
    );
}

function SummaryPanel({ scoring, weakerAreas }) {
    return (
        <div className="border border-[#E5E2DC] rounded-lg p-5 bg-white flex flex-col gap-3"
             data-testid="ws2-summary">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-[11px] uppercase text-[#666] tracking-wide">
                        System Assessment — Advisory Only
                    </div>
                    <div className="font-manrope font-semibold text-[15px]">
                        2. Assessment Summary
                    </div>
                </div>
                <div className="text-[12px] text-[#666]" data-testid="ws2-summary-progress">
                    {scoring.assessed_count} of {scoring.categories_total} assessed
                </div>
            </div>
            <div className="grid grid-cols-3 gap-4 text-[13px]">
                <div>
                    <div className="text-[11px] uppercase text-[#666]">Score</div>
                    <div className="text-[20px] font-manrope" data-testid="ws2-summary-total">
                        {scoring.total}/{scoring.max_total}
                    </div>
                </div>
                <div>
                    <div className="text-[11px] uppercase text-[#666]">Percentage</div>
                    <div className="text-[20px] font-manrope" data-testid="ws2-summary-percentage">
                        {scoring.percentage != null ? `${scoring.percentage}%` : "—"}
                    </div>
                </div>
                <div>
                    <div className="text-[11px] uppercase text-[#666]">Band</div>
                    <div className="text-[20px] font-manrope" data-testid="ws2-summary-band">
                        {scoring.band ? BAND_DISPLAY[scoring.band] : "—"}
                    </div>
                </div>
            </div>
            {scoring.recommendation && (
                <div className="border-t border-[#E5E2DC] pt-3">
                    <div className="text-[11px] uppercase text-[#666] mb-1">Recommendation</div>
                    <div className="text-[13px] whitespace-pre-wrap" data-testid="ws2-summary-recommendation">
                        {scoring.recommendation}
                    </div>
                </div>
            )}
            {weakerAreas && weakerAreas.length > 0 && (
                <div>
                    <div className="text-[11px] uppercase text-[#666] mb-1">Weaker assessment areas identified</div>
                    <ul className="list-disc list-inside text-[13px]" data-testid="ws2-summary-weaker-areas">
                        {weakerAreas.map(k => {
                            const c = CATEGORIES.find(x => x.key === k);
                            return <li key={k}>{c ? c.display : k}</li>;
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
}

function CommercialContextPanel({ commercial }) {
    if (!commercial) return null;
    const FSI_DISPLAY = {
        free_design_only: "Free Design only",
        unsure: "Unsure at this stage",
        further_design_technical: "Further design + technical",
        project_management: "Project management",
        construction_design_build: "Construction (design & build)",
        tailored_combination: "Tailored combination",
    };
    return (
        <div className="border border-[#E5E2DC] rounded-lg p-5 bg-[#FBFAF6] flex flex-col gap-3"
             data-testid="ws2-commercial-context">
            <div className="flex items-center justify-between">
                <div className="font-manrope font-semibold text-[15px]">
                    3. Commercial Context
                </div>
                <span className="text-[11px] uppercase text-[#666] border border-[#E5E2DC] px-2 py-0.5 rounded-full bg-white"
                    data-testid="ws2-commercial-not-scored">Not Scored</span>
            </div>
            <div className="text-[12.5px] text-[#666]">
                Read-only context from Workstage 1. These do NOT contribute
                to the advisory score.
            </div>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-[13px]">
                <div>
                    <dt className="text-[11px] uppercase text-[#666]">Lead Quality</dt>
                    <dd data-testid="ws2-commercial-lead-quality">
                        {commercial.lead_quality || "—"}
                    </dd>
                </div>
                <div>
                    <dt className="text-[11px] uppercase text-[#666]">Future Service Intention</dt>
                    <dd data-testid="ws2-commercial-fsi">
                        {commercial.future_service_intention
                            ? (FSI_DISPLAY[commercial.future_service_intention]
                                || commercial.future_service_intention)
                            : "—"}
                    </dd>
                </div>
            </dl>
            {commercial.fsi_note && (
                <div>
                    <div className="text-[11px] uppercase text-[#666]">FSI note / context</div>
                    <div className="text-[13px] whitespace-pre-wrap" data-testid="ws2-commercial-fsi-note">
                        {commercial.fsi_note}
                    </div>
                </div>
            )}
        </div>
    );
}

function CommsModal({ projectId, initialTemplate, onClose, onSent }) {
    const [templateCode, setTemplateCode] = useState(initialTemplate);
    const [messageBody, setMessageBody] = useState("");
    const [subjectOverride, setSubjectOverride] = useState("");
    const [preview, setPreview] = useState(null);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const opId = useMemo(() => `ws2-comm-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, []);
    const buildBody = () => ({
        template_code: templateCode,
        merge_values: { message_body: messageBody },
        subject_override: subjectOverride || null,
    });
    const runPreview = async () => {
        setErr(""); setBusy(true);
        try {
            const r = await api.post(
                `/admin/projects/${projectId}/ws2/comms/preview`, buildBody());
            setPreview(r.data);
        } catch (e) {
            const d = e?.response?.data?.detail;
            setErr(typeof d === "string" ? d : (d?.error || "Preview failed"));
        } finally { setBusy(false); }
    };
    const confirmSend = async () => {
        setErr(""); setBusy(true);
        try {
            await api.post(
                `/admin/projects/${projectId}/ws2/comms/send`,
                { ...buildBody(), create_op_id: opId });
            toast.success("Communication sent");
            onSent && onSent(templateCode);
            onClose();
        } catch (e) {
            const d = e?.response?.data?.detail;
            setErr(typeof d === "string" ? d : (d?.error || "Send failed"));
        } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
             data-testid="ws2-comms-modal">
            <div className="bg-white rounded-lg shadow-lg w-[640px] max-w-[92vw] max-h-[92vh] overflow-y-auto p-4 flex flex-col gap-3 border border-[#E5E2DC]">
                <div className="font-manrope font-semibold text-[15px]">
                    Send Communication
                </div>
                {!preview && (
                    <>
                        <label className="flex flex-col gap-1">
                            <span className="text-[11px] uppercase text-[#666]">Template</span>
                            <select value={templateCode}
                                onChange={e => setTemplateCode(e.target.value)}
                                data-testid="ws2-comms-template"
                                className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]">
                                <option value="ws2_proceed_to_free_design">Proceed to Free Design</option>
                                <option value="ws2_feasibility_review_update">Feasibility Review Update</option>
                                <option value="ws2_unable_to_progress">Unable to Progress</option>
                                <option value="ws2_general_communication">General Communication</option>
                            </select>
                        </label>
                        <label className="flex flex-col gap-1">
                            <span className="text-[11px] uppercase text-[#666]">Subject override (optional)</span>
                            <input value={subjectOverride}
                                onChange={e => setSubjectOverride(e.target.value)}
                                data-testid="ws2-comms-subject"
                                className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
                        </label>
                        <label className="flex flex-col gap-1">
                            <span className="text-[11px] uppercase text-[#666]">Message body</span>
                            <textarea rows={5} value={messageBody}
                                onChange={e => setMessageBody(e.target.value)}
                                data-testid="ws2-comms-body"
                                className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
                        </label>
                    </>
                )}
                {preview && (
                    <div className="flex flex-col gap-2 border border-[#E5E2DC] rounded-md p-3 bg-[#FBFAF6]"
                         data-testid="ws2-comms-preview">
                        <div className="text-[11px] uppercase text-[#666]">Authoritative preview</div>
                        <div className="text-[12px]">
                            <span className="text-[#666]">To:</span> {preview.recipient?.name} &lt;{preview.recipient?.address}&gt;
                        </div>
                        <div className="text-[12px]">
                            <span className="text-[#666]">Subject:</span>{" "}
                            <span data-testid="ws2-comms-preview-subject">{preview.rendered_subject}</span>
                        </div>
                        <pre className="whitespace-pre-wrap text-[12.5px] font-mono bg-white border border-[#E5E2DC] rounded-md p-2 max-h-[38vh] overflow-y-auto"
                             data-testid="ws2-comms-preview-body">{preview.rendered_body}</pre>
                    </div>
                )}
                {err && <div className="text-[12px] text-terracotta" data-testid="ws2-comms-error">{err}</div>}
                <div className="flex justify-end gap-2">
                    {!preview && (
                        <>
                            <button onClick={onClose} disabled={busy}
                                data-testid="ws2-comms-cancel"
                                className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md">
                                Cancel
                            </button>
                            <button onClick={runPreview} disabled={busy}
                                data-testid="ws2-comms-preview-btn"
                                className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md disabled:opacity-40">
                                {busy ? "Preparing…" : "Preview"}
                            </button>
                        </>
                    )}
                    {preview && (
                        <>
                            <button onClick={() => setPreview(null)} disabled={busy}
                                data-testid="ws2-comms-preview-back"
                                className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md">
                                Back to edit
                            </button>
                            <button onClick={confirmSend} disabled={busy}
                                data-testid="ws2-comms-send"
                                className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md disabled:opacity-40">
                                {busy ? "Sending…" : "Confirm & Send"}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

/* Reusable WS2 modules cluster.  Renders the four vertical WS2
   modules only — outer page chrome (header, back-nav) is the
   canonical Project Workspace's responsibility. */
export function WS2ActiveModules({ projectId, mode = "active", onCompleted }) {
    const id = projectId;
    const navigate = useNavigate();
    const [ws, setWs] = useState(null);
    const [err, setErr] = useState("");
    const [pendingScores, setPendingScores] = useState({});
    const [pendingSummary, setPendingSummary] = useState("");
    const [pendingNextAction, setPendingNextAction] = useState("");
    const [pendingDecision, setPendingDecision] = useState("");
    const [pendingRationale, setPendingRationale] = useState("");
    const [busy, setBusy] = useState(false);
    const [commsOpen, setCommsOpen] = useState(null);
    const load = async () => {
        try {
            const r = await api.get(`/admin/projects/${id}/ws2`);
            setWs(r.data);
            setPendingScores(r.data.scores || {});
            setPendingSummary(r.data.feasibility_summary || "");
            setPendingNextAction(r.data.next_action || "");
            setPendingDecision(r.data.admin_decision || "");
            setPendingRationale(r.data.decision_rationale || "");
        } catch (e) {
            const d = e?.response?.data?.detail;
            setErr(typeof d === "string" ? d : (d?.detail || d?.error || "Failed to load WS2"));
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
    const opId = () => `ws2-op-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const saveScore = async (k, v) => {
        setBusy(true);
        const nextScores = { ...pendingScores, [k]: v };
        setPendingScores(nextScores);
        try {
            const r = await api.post(`/admin/projects/${id}/ws2/draft`, {
                scores: { [k]: v },
                expected_record_version: ws.record_version,
                create_op_id: opId(),
            });
            setWs(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail?.error || "Save failed");
            load();
        } finally { setBusy(false); }
    };
    const saveSummary = async () => {
        setBusy(true);
        try {
            const r = await api.post(`/admin/projects/${id}/ws2/draft`, {
                feasibility_summary: pendingSummary,
                next_action: pendingNextAction,
                expected_record_version: ws.record_version,
                create_op_id: opId(),
            });
            setWs(r.data);
            toast.success("Draft saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail?.error || "Save failed");
        } finally { setBusy(false); }
    };
    const commitDecision = async () => {
        if (!pendingDecision) return;
        setBusy(true);
        try {
            const r = await api.post(`/admin/projects/${id}/ws2/decision`, {
                decision: pendingDecision,
                decision_rationale: pendingRationale || null,
                next_action: pendingNextAction || null,
                expected_record_version: ws.record_version,
                create_op_id: opId(),
            });
            setWs(r.data);
            toast.success("Decision recorded");
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error(d?.detail || d?.error || "Failed");
        } finally { setBusy(false); }
    };
    const complete = async () => {
        setBusy(true);
        try {
            await api.post(`/admin/projects/${id}/ws2/complete`, {
                expected_record_version: ws.record_version,
                create_op_id: opId(),
            });
            toast.success("Workstage 2 completed");
            if (onCompleted) onCompleted();
        } catch (e) {
            const d = e?.response?.data?.detail;
            if (Array.isArray(d?.blockers)) {
                toast.error(d.blockers.map(b => b.detail).join(" · "));
            } else {
                toast.error(d?.detail || d?.error || "Failed");
            }
            load();
        } finally { setBusy(false); }
    };
    if (err) return <div className="text-[13px] text-terracotta" data-testid="ws2-error">{err}</div>;
    if (!ws) return <div className="text-[13px] text-[#666]" data-testid="ws2-loading">Loading Feasibility…</div>;
    const scoring = ws.scoring || { total: 0, max_total: 12,
        assessed_count: 0, categories_total: 6, weaker_areas: [] };
    const isCompleted = ws.state === "completed";
    // §28 — contextual entry points to shared systems (§ correction §2:
    // the Follow-up action opens the canonical A3/B3 Follow-up dialog
    // via the workspace right-rail — NOT a free-text ID input here).
    const decisionActions = {
        proceed: [{ code: "ws2_proceed_to_free_design",
                     label: "Send Proceed to Free Design" }],
        further_review: [
            { code: "ws2_feasibility_review_update",
              label: "Send Feasibility Review Update" },
            { code: "__rfi__", label: "Request Information",
              onClick: () => navigate(`/admin/projects/${id}/rfi`) },
        ],
        decline: [{ code: "ws2_unable_to_progress",
                     label: "Send Unable to Progress" }],
    }[ws.admin_decision] || [];
    // D5 Slice B — coherent mode propagation.  See B4Pages.jsx WS1
    // for the pattern.
    const readOnly = mode !== "active";
    return (
        <fieldset disabled={readOnly} className="border-0 p-0 m-0 min-w-0"
                  data-testid={`ws2-fieldset-mode-${mode}`}>
        {readOnly && (
            <div className="mb-3 px-3 py-2 rounded-md bg-[#F5F1EA] border border-[#E5E2DC] text-[12px] text-[#666]"
                 data-testid={`ws2-mode-banner-${mode}`}>
                {mode === "future"
                    ? "This Workstage is not yet active. Content is shown for reference only."
                    : "This Workstage is completed. Historical data shown, read-only."}
            </div>
        )}
        <div className="flex flex-col gap-3" data-testid="ws2-page">
            {isCompleted && (
                <div className="border border-[#E5E2DC] bg-[#F5F3EE] rounded-md p-3 text-[13px]"
                     data-testid="ws2-completed-banner">
                    Feasibility — completed{" "}
                    {ws.completed_at ? `on ${ws.completed_at.slice(0, 10)}` : ""}
                    {scoring.band && ` · ${BAND_DISPLAY[scoring.band]}`}
                    {scoring.percentage != null && ` · ${scoring.percentage}%`}
                    {ws.admin_decision && ` · Outcome: ${ws.admin_decision}`}
                </div>
            )}

            {/* Module 1 — Feasibility Assessment */}
            <section className="bg-white border border-[#E5E2DC] rounded-lg p-5 flex flex-col gap-3"
                     data-testid="ws2-module-assessment">
                <h3 className="font-manrope font-semibold text-[14px]">
                    1. Feasibility Assessment
                </h3>
                <div className="text-[12px] text-[#666]">
                    Score each area 0 / 1 / 2. Category text is authoritative
                    — read it carefully. Scores are advisory only.
                </div>
                <div className="grid grid-cols-2 gap-3">
                    {CATEGORIES.map(c => (
                        <CategoryCard key={c.key} cat={c}
                            value={pendingScores[c.key]}
                            onChange={saveScore}
                            disabled={busy || isCompleted} />
                    ))}
                </div>
                <label className="flex flex-col gap-1 mt-2">
                    <span className="text-[11px] uppercase text-[#666]">
                        Feasibility summary (internal, admin only)
                    </span>
                    <textarea rows={3}
                        value={pendingSummary}
                        onChange={e => setPendingSummary(e.target.value)}
                        disabled={isCompleted}
                        data-testid="ws2-feasibility-summary"
                        className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
                </label>
                <div className="flex justify-end">
                    <button onClick={saveSummary} disabled={busy || isCompleted}
                        data-testid="ws2-save-summary"
                        className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md disabled:opacity-40">
                        Save summary
                    </button>
                </div>
            </section>

            {/* Module 2 — Assessment Summary */}
            <SummaryPanel scoring={scoring} weakerAreas={scoring.weaker_areas} />

            {/* Module 3 — Commercial Context (Not Scored) */}
            <CommercialContextPanel commercial={ws.commercial_context} />

            {/* Module 4 — Admin Decision & Completion */}
            <section className="bg-white border border-[#E5E2DC] rounded-lg p-5 flex flex-col gap-3"
                     data-testid="ws2-module-decision">
                <h3 className="font-manrope font-semibold text-[14px]">
                    4. Admin Decision &amp; Workstage Completion
                </h3>
                <div className="text-[12px] text-[#666]">
                    The system NEVER auto-selects the decision. Choose
                    deliberately.
                </div>
                <div className="flex flex-col gap-1.5">
                    {DECISIONS.map(d => (
                        <label key={d.value}
                            className="flex items-center gap-2 text-[13px] cursor-pointer">
                            <input type="radio" name="ws2-decision"
                                checked={pendingDecision === d.value}
                                disabled={isCompleted}
                                onChange={() => setPendingDecision(d.value)}
                                data-testid={`ws2-decision-${d.value}`}
                                className="accent-charcoal" />
                            {d.label}
                        </label>
                    ))}
                </div>
                <label className="flex flex-col gap-1">
                    <span className="text-[11px] uppercase text-[#666]">
                        Rationale (required when decision materially
                        contradicts the advisory)
                    </span>
                    <textarea rows={2}
                        value={pendingRationale}
                        onChange={e => setPendingRationale(e.target.value)}
                        disabled={isCompleted}
                        data-testid="ws2-decision-rationale"
                        className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
                </label>
                <label className="flex flex-col gap-1">
                    <span className="text-[11px] uppercase text-[#666]">
                        Next Action
                    </span>
                    <input value={pendingNextAction}
                        onChange={e => setPendingNextAction(e.target.value)}
                        disabled={isCompleted}
                        data-testid="ws2-next-action"
                        className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
                </label>
                <div className="text-[11.5px] text-[#666]" data-testid="ws2-followup-hint">
                    Need to set a Follow-up? Use the canonical{" "}
                    <span className="font-medium">Set Follow-up</span>{" "}
                    action in the right-hand rail — it uses the same
                    global Follow-up service across every workstage.
                </div>
                <div className="flex justify-end gap-2">
                    <button onClick={commitDecision}
                        disabled={busy || isCompleted || !pendingDecision}
                        data-testid="ws2-record-decision"
                        className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md disabled:opacity-40">
                        Record decision
                    </button>
                </div>
                {ws.admin_decision && !isCompleted && (
                    <div className="border-t border-[#E5E2DC] pt-3 flex flex-col gap-2">
                        <div className="text-[11px] uppercase text-[#666]">
                            Decision-specific actions
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {decisionActions.map(a =>
                                a.onClick ? (
                                    <button key={a.code}
                                        onClick={a.onClick}
                                        data-testid={`ws2-action-${a.code}`}
                                        className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md">
                                        {a.label}
                                    </button>
                                ) : (
                                    <button key={a.code}
                                        onClick={() => setCommsOpen(a.code)}
                                        data-testid={`ws2-action-${a.code}`}
                                        className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md">
                                        {a.label}
                                    </button>
                                )
                            )}
                        </div>
                        {ws.admin_decision === "proceed" && (
                            <div className="text-[11.5px] text-[#666]">
                                Proceed communication{" "}
                                {ws.proceed_communication_id
                                    ? "sent — WS2 may be completed."
                                    : "outstanding — required before completing WS2."}
                            </div>
                        )}
                        {ws.admin_decision === "proceed" && (
                            <MarkSentExternallyControl
                                testid="ws2-proceed-mark-sent-externally"
                                endpointUrl={`${API}/admin/projects/${id}/ws2/comms/mark-sent-externally`}
                                buildBody={({ sent_on, notes, op_id }) => ({
                                    template_code: "ws2_proceed_to_free_design",
                                    sent_on, notes, op_id,
                                })}
                                alreadySatisfied={!!ws.proceed_communication_id}
                                onSuccess={load} />
                        )}
                        {ws.admin_decision === "decline" && (
                            <div className="text-[11.5px] text-[#666]">
                                Decline communication{" "}
                                {ws.decline_communication_id
                                    ? "sent."
                                    : "outstanding — required before completing WS2."}
                            </div>
                        )}
                        {ws.admin_decision === "decline" && (
                            <MarkSentExternallyControl
                                testid="ws2-decline-mark-sent-externally"
                                endpointUrl={`${API}/admin/projects/${id}/ws2/comms/mark-sent-externally`}
                                buildBody={({ sent_on, notes, op_id }) => ({
                                    template_code: "ws2_unable_to_progress",
                                    sent_on, notes, op_id,
                                })}
                                alreadySatisfied={!!ws.decline_communication_id}
                                onSuccess={load} />
                        )}
                        {ws.admin_decision === "proceed" && (
                            <div className="flex justify-end">
                                <button onClick={complete}
                                    disabled={busy || !ws.proceed_communication_id}
                                    data-testid="ws2-complete"
                                    className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md disabled:opacity-40">
                                    Complete Workstage 2
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </section>

            {commsOpen && (
                <CommsModal projectId={id}
                    initialTemplate={commsOpen}
                    onClose={() => setCommsOpen(null)}
                    onSent={() => load()} />
            )}
        </div>
        </fieldset>
    );
}

/* § correction §1 — the dedicated `/ws2` route was only ever a
   development deep-link.  Production identity is the canonical
   `/admin/projects/:id` workspace, which now embeds WS2 inline via
   `WS2ActiveModules`.  We keep this component as a permanent
   compatibility redirect so any old bookmark or right-rail
   navigation collapses back into the canonical URL. */
export function WS2Page() {
    const { id } = useParams();
    return <Navigate to={`/admin/projects/${id}`} replace />;
}

export default WS2Page;
